import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, map, of } from 'rxjs';
import { AuthService } from '@core/services/api/auth.service';
import { PreviewSessionService } from '@core/services/preview-session.service';
import { minRoleGuard } from '@core/guards/min-role.guard';

/**
 * Access guard for `/tasks/do/:id`. It keeps the existing role-based behaviour
 * for logged-in users (delegating to `minRoleGuard`) and additionally allows an
 * anonymous, read-only preview when a valid preview link/session is present.
 * `minRoleGuard` itself is never modified.
 */
export const taskDoAccessGuard: CanActivateFn = (route, state) => {
  const auth = inject(AuthService);
  const preview = inject(PreviewSessionService);
  const router = inject(Router);
  const taskId = route.paramMap.get('id');

  // 1) A real session always wins — unchanged role-based behaviour.
  if (auth.isAuthenticated()) {
    return minRoleGuard(route, state);
  }

  // 2) Active preview for exactly this task — render the read-only view.
  if (preview.isActive() && preview.getTaskId() === taskId) {
    return true;
  }

  // 3) Fresh deeplink carrying ?pt=<token> — exchange it, then redirect to the
  //    clean URL (token stripped). The follow-up navigation hits case 2.
  const previewToken = route.queryParamMap.get('pt');
  if (previewToken && taskId) {
    return preview.exchange(previewToken).pipe(
      map(() => router.createUrlTree(['/tasks/do', taskId])),
      catchError(() => of(preview.loginUrlTree(state.url))),
    );
  }

  // 4) Neither session nor preview token — unchanged login flow.
  return minRoleGuard(route, state);
};
